import { Check, ExternalLink, LoaderCircle, Undo2, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { useOtcTiming } from "../../hooks/useOtcTiming";
import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { chunkAddress } from "../../utils/cryptoSwapAddress";
import { CRYPTO_SWAPS_HISTORY_PATH, CRYPTO_SWAPS_PATH } from "../../utils/cryptoSwapLinks";
import { bucketForStatus } from "../../utils/cryptoSwapStatus";
import { AssetAmount } from "../shared/AssetAmount";
import { AssetAvatar } from "../shared/CryptoSwapBits";
import { OperationEta } from "../shared/OperationEta";
import { BuyTransit } from "../shared/OtcTransit";

export interface OrderOutcomeProps {
  order: OtcOrder;
}

/** Where the order stands, in one panel: on its way, delivered, or failed with the COP back in the wallet. */
export function OrderOutcome({ order }: OrderOutcomeProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const bucket = bucketForStatus(order.status);
  const txUrl = labels.txUrl(order.destination.network, order.destination.tx_hash);
  const explorer = labels.networkRule(order.destination.network)?.explorer ?? "";
  const timing = useOtcTiming();

  const actions = (
    <div className="flex flex-wrap gap-2">
      <ColocaButton variant="primary" render={<Link to={CRYPTO_SWAPS_PATH} />}>
        {t("page_new_order")}
      </ColocaButton>
      <ColocaButton variant="default" render={<Link to={CRYPTO_SWAPS_HISTORY_PATH} />}>
        {t("order_view_history")}
      </ColocaButton>
    </div>
  );

  if (bucket === "done") {
    return (
      <OutcomeShell icon={Check} tone="success" title={t("outcome_paid_title")}>
        <Delivered order={order} />
        {txUrl && explorer && (
          <a
            href={txUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary hover:underline"
          >
            {t("summary_view_explorer", { explorer })}
            <ExternalLink aria-hidden="true" className="size-3.5" />
          </a>
        )}
        {actions}
      </OutcomeShell>
    );
  }

  if (bucket === "failed") {
    return (
      <OutcomeShell icon={Undo2} tone="warning" title={t("outcome_failed_title")}>
        <p className="text-base leading-relaxed text-foreground">{t("outcome_failed_message")}</p>
        <div className="flex flex-col gap-1 rounded-2xl bg-muted p-4">
          <span className="text-xs text-muted-foreground">{t("outcome_failed_returned")}</span>
          <span className="flex flex-wrap items-baseline gap-1.5">
            <AssetAmount
              value={order.send.amount}
              currency={order.send.currency}
              className="text-xl font-semibold"
            />
            <span className="text-sm text-muted-foreground">{order.send.currency}</span>
          </span>
        </div>
        {actions}
      </OutcomeShell>
    );
  }

  return (
    <OutcomeShell icon={LoaderCircle} tone="info" title={t("outcome_live_title")} spinning>
      <BuyTransit
        className="max-w-lg"
        sendCurrency={order.send.currency}
        receiveCurrency={order.receive.currency}
        network={order.destination.network}
        address={order.destination.address}
      />
      <p className="text-base leading-relaxed text-foreground">
        {t("outcome_live_message", {
          currency: order.receive.currency,
          network: labels.networkShort(order.destination.network),
        })}
      </p>
      <OperationEta since={order.created_at} estimate={timing?.buy[order.destination.network]} />
      <Delivered order={order} />
    </OutcomeShell>
  );
}

function Delivered({ order }: { order: OtcOrder }) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { head, middle, tail } = chunkAddress(order.destination.address);

  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-muted p-4">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <AssetAvatar asset={order.receive.currency} kind="ONCHAIN" size="sm" />
        <AssetAmount
          value={order.receive.amount}
          currency={order.receive.currency}
          className="text-2xl font-semibold"
        />
        <span className="text-sm text-muted-foreground">{order.receive.currency}</span>
      </span>
      {order.destination.address && (
        <span className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">
            {t("outcome_to_address", { network: labels.networkShort(order.destination.network) })}
          </span>
          <span className="sr-only">{order.destination.address}</span>
          <span aria-hidden="true" className="flex flex-wrap gap-x-2 font-mono text-sm tabular-nums">
            <span className="font-semibold text-foreground">{head}</span>
            {middle.map((group, index) => (
              <span key={`${group}-${index}`} className="text-muted-foreground">
                {group}
              </span>
            ))}
            <span className="font-semibold text-foreground">{tail}</span>
          </span>
        </span>
      )}
    </div>
  );
}

const OUTCOME_TONE_CLASS = {
  success: "bg-success/10 text-success",
  warning: "bg-warning-container text-warning-container-foreground",
  info: "bg-primary/10 text-primary",
  neutral: "bg-muted text-muted-foreground",
} as const;

export function OutcomeShell({
  icon: Icon,
  tone,
  title,
  spinning,
  children,
}: {
  icon: LucideIcon;
  tone: keyof typeof OUTCOME_TONE_CLASS;
  title: string;
  spinning?: boolean;
  children: ReactNode;
}) {
  return (
    <ColocaSurface className="flex flex-col gap-5 rounded-3xl p-6 sm:p-8">
      <div className="flex items-center gap-4">
        <span
          className={cn(
            "flex size-14 shrink-0 items-center justify-center rounded-full",
            OUTCOME_TONE_CLASS[tone],
          )}
        >
          <Icon
            aria-hidden="true"
            className={cn("size-6", spinning && "animate-spin motion-reduce:animate-none")}
          />
        </span>
        <h2 className="text-3xl leading-tight font-semibold tracking-tight text-foreground">{title}</h2>
      </div>
      {children}
    </ColocaSurface>
  );
}
